import type { RawVideo, VideoRecord, VideoItemData } from '@/types/video';

const slugify = (s: string) =>
    s.toLowerCase()
        .normalize('NFKD')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

function parseVideoTs(v?: number | string): number | null {
    if (v == null || v === '') return null;

    if (typeof v === 'number') {
        if (!Number.isFinite(v)) return null;
        return v < 1e12 ? Math.round(v * 1000) : Math.round(v);
    }

    const numeric = Number(v);
    if (Number.isFinite(numeric)) {
        return numeric < 1e12 ? Math.round(numeric * 1000) : Math.round(numeric);
    }

    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;

    const [, yy, MM, dd, hh, mm, ss] = m;
    const d = new Date(+yy, +MM - 1, +dd, +hh, +mm, +(ss ?? '0'), 0);

    return Number.isFinite(d.getTime()) ? d.getTime() : null;
}

export function toRecord(v: RawVideo): VideoRecord {
    const id = Number(v.id);
    const guestIds = Array.isArray(v.podcast_guest_ids)
        ? v.podcast_guest_ids.filter((x): x is number => typeof x === 'number')
        : [];

    if (!Number.isFinite(id)) throw new Error('Video without numeric id');

    const releaseTs = parseVideoTs(v.release_date);
    const releasedTs = parseVideoTs(v.released);
    const base = v.title ? slugify(String(v.title)) : '';
    const slug = base ? `${id}-${base}` : String(id);

    const podcastIds = (v.podcast_pro_ids ?? [])
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n));

    const upcoming = v.upcoming ?? (releaseTs != null && releaseTs > Date.now());

    return {
        id,
        slug,
        thumb: v.thumb,
        title: v.title,
        subtitle: v.subtitle,
        description: v.description,
        podcast_pro_ids: podcastIds,
        podcast_guest_ids: guestIds,
        released: v.released,
        release_date: v.release_date,
        upcoming,
        releasedTs,
        releaseTs,
    };
}

export function toItem(rec: VideoRecord): VideoItemData {
    return {
        id: rec.id,
        slug: rec.slug,
        thumb: rec.thumb,
        title: rec.title,
        subtitle: rec.subtitle,
        podcast_guest_ids: rec.podcast_guest_ids,
        released: rec.released,
        release_date: rec.release_date,
        releasedTs: rec.releasedTs,
        releaseTs: rec.releaseTs,
        upcoming: rec.upcoming,
    };
}

export function normalizeArray(raw: unknown): RawVideo[] {
    if (Array.isArray(raw)) return raw as RawVideo[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).items)) {
        return (raw as any).items as RawVideo[];
    }
    return [];
}
